import React, { useEffect, useState } from "react";
import { mergeRight } from "ramda";
import { useCreateSipServer, useUpdateSipServer } from "../sipServerServices";

import makeStyles from "@mui/styles/makeStyles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContentText from "@mui/material/DialogContentText";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

import DialogContent from "@mui/material/DialogContent";
import { lensProp, pick, set } from "ramda";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import { FetchError } from "../../../util/fetchUtils";
import { useDispatch } from "react-redux";
import { logOut } from "../../../auth/authActions";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";

const Alert = React.forwardRef((props, ref) => {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const useStyles = makeStyles(theme => ({
  checkbox: {
    paddingTop: theme.spacing(1),
    marginLeft: theme.spacing(2),
  },
}));

const initialState = {
  fields: {
    hostname: "",
    port: "",
    version: "VERSION_1_00",
    institutionId: "",
    location: "",
    username: "",
    password: "",
    checksum: false,
  },
};

const EditSipServer = props => {
  const { server, open, onClose, onSave } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const [fields, setFields] = useState(initialState.fields);
  const [errorAlert, setErrorAlert] = useState({ open: false });

  useEffect(() => {
    if (server && server.sipServerId) {
      setFields(
        pick(
          [
            "hostname",
            "port",
            "version",
            "institutionId",
            "location",
            "username",
            "password",
            "checksum",
          ],
          server,
        ),
      );
    } else {
      setFields(initialState.fields);
    }
  }, [server]);

  const onFieldChanged = field => (event, optVal) => {
    const value = event.target.value || optVal;
    const update = set(lensProp(field), value, fields);
    setFields(update);
  };

  const showAlert = message =>
    setErrorAlert({
      open: true,
      message,
    });

  const updateSipServer = useUpdateSipServer();
  const updateServer = async server => {
    await updateSipServer.mutateAsync({
      resourceIds: { sipServerId: server.sipServerId },
      data: server,
    });
  };

  const createSipServer = useCreateSipServer();
  const createServer = async server => {
    await createSipServer.mutateAsync({ data: server });
  };

  const handleError = message => err => {
    if (err instanceof FetchError) {
      if (err.status === 401) {
        dispatch(logOut());
      } else {
        showAlert(message);
      }
    } else {
      showAlert(err.message);
    }
  };

  const handleOnSave = async () => {
    const mergedServer = mergeRight(server, fields);

    try {
      if (mergedServer.sipServerId) {
        await updateServer(mergedServer);
      } else {
        await createServer(mergedServer);
      }

      onSave(mergedServer);
    } catch (err) {
      handleError("Unable to save SIP Server.")(err);
    }
  };

  const handleOnClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleOnClose}
      aria-labelledby="form-dialog-title"
    >
      {server.sipServerId ? (
        <DialogTitle id="form-dialog-title">
          SIP Server ({server.sipServerId})
        </DialogTitle>
      ) : (
        <DialogTitle id="form-dialog-title">New SIP Server</DialogTitle>
      )}
      <DialogContent>
        <DialogContentText>
          Specify the SIP Server Information.
        </DialogContentText>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              autoFocus
              id="hostname"
              label="Hostname"
              fullWidth
              value={fields.hostname || ""}
              onChange={onFieldChanged("hostname")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="port"
              label="Port"
              fullWidth
              value={fields.port || ""}
              onChange={onFieldChanged("port")}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel htmlFor="sip-server-version">Version</InputLabel>
              <Select
                labelId="sip-server-version"
                id="version"
                value={fields.version}
                onChange={onFieldChanged("version")}
              >
                <MenuItem value="VERSION_1_00">VERSION_1_00</MenuItem>
                <MenuItem value="VERSION_2_00">VERSION_2_00</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <FormControlLabel
                classes={{ root: classes.checkbox }}
                control={
                  <Checkbox
                    checked={fields.checksum}
                    onChange={onFieldChanged("checksum")}
                    name="checksum"
                    color="primary"
                  />
                }
                label="Checksum"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="institutionId"
              label="Institution Id"
              fullWidth
              value={fields.institutionId || ""}
              onChange={onFieldChanged("institutionId")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="location"
              label="Location"
              fullWidth
              value={fields.location || ""}
              onChange={onFieldChanged("location")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="username"
              label="Username"
              fullWidth
              value={fields.username || ""}
              onChange={onFieldChanged("username")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="password"
              label="Password"
              fullWidth
              value={fields.password || ""}
              onChange={onFieldChanged("password")}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOnClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleOnSave} color="primary">
          Save
        </Button>
      </DialogActions>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={errorAlert.open}
        autoHideDuration={6000}
        onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
      >
        <Alert
          onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
          severity="error"
        >
          {errorAlert.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default EditSipServer;
